import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { PORTAL } from "@/constants/testIds";
import {
  Activity, Mail, CheckCircle2, AlertCircle, FileText,
  ArrowRight, Clock, Loader, RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

function StatusBadge({ status }) {
  const map = {
    normal:             "badge-normal",
    requires_attention: "badge-requires",
    critical:           "badge-abnormal",
  };
  const labels = { normal: "Normal", requires_attention: "Needs Attention", critical: "Critical" };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[status] || "badge-normal"}`}>
      {labels[status] || status}
    </span>
  );
}

// ── Entry screen — request magic link ──────────────────────────────────────

function RequestAccessScreen({ onSent }) {
  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/portal/request-access`, { email: email.trim() });
      onSent(email.trim());
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-blue-900 flex items-center justify-center mx-auto mb-5">
          <Activity size={26} className="text-white" />
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Your Health Reports
        </h1>
        <p className="text-slate-500 text-sm leading-relaxed max-w-sm mx-auto">
          Access blood test reports that your doctor has shared with you — no account needed.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-7 shadow-sm">
        <h2 className="text-base font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          Get your reports
        </h2>
        <p className="text-slate-500 text-sm mb-5">
          Enter the email address your doctor used when sharing your report.
        </p>

        {error && (
          <div data-testid={PORTAL.errorMessage}
            className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4">
            <AlertCircle size={15} className="flex-shrink-0" /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
            <input
              data-testid={PORTAL.emailInput}
              type="email" required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
          <button
            data-testid={PORTAL.requestButton}
            type="submit" disabled={loading}
            className="w-full bg-blue-900 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-blue-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {loading
              ? <><Loader size={15} className="animate-spin" /> Sending…</>
              : <><Mail size={15} /> Send me my reports</>}
          </button>
        </form>
      </div>

      <p className="text-center text-xs text-slate-400 mt-6 leading-relaxed">
        Reports are shared directly by your healthcare provider via LabReport AI.
        This link is for read-only access.
      </p>
    </div>
  );
}

// ── Email sent confirmation screen ─────────────────────────────────────────

function EmailSentScreen({ email, onBack }) {
  return (
    <div data-testid={PORTAL.sentConfirmation} className="w-full max-w-md text-center">
      <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 size={32} className="text-emerald-600" />
      </div>
      <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
        Check your email
      </h2>
      <p className="text-slate-500 text-sm mb-2">We sent a secure link to:</p>
      <p className="font-semibold text-slate-800 text-sm mb-6">{email}</p>
      <p className="text-slate-400 text-xs leading-relaxed mb-8 max-w-xs mx-auto">
        The link expires in 24 hours. If you don't see it, check your spam folder.
        Only emails that have had reports shared with them will receive a link.
      </p>
      <button onClick={onBack} className="text-blue-900 text-sm font-medium hover:underline">
        Try a different email
      </button>
    </div>
  );
}

// ── Reports list screen ────────────────────────────────────────────────────

function ReportsScreen({ email, reports, onRequestNew }) {
  if (reports.length === 0) {
    return (
      <div className="w-full max-w-lg text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <FileText size={20} className="text-slate-400" />
        </div>
        <h3 className="text-base font-medium text-slate-700 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          No reports found
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          No shared reports were found for <strong>{email}</strong>.
          Ask your doctor to share a report with this email address.
        </p>
        <button onClick={onRequestNew}
          className="text-blue-900 text-sm font-medium hover:underline">
          Try a different email
        </button>
      </div>
    );
  }

  return (
    <div data-testid={PORTAL.reportsList} className="w-full max-w-2xl">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          Your Reports
        </h2>
        <p className="text-slate-400 text-sm">
          {reports.length} report{reports.length !== 1 ? "s" : ""} shared with <strong>{email}</strong>
        </p>
      </div>

      <div className="space-y-3">
        {reports.map((r, i) => {
          const patientName = r.patient_info?.name;
          const shareUrl = `/shared/${r.share_token}`;
          return (
            <div
              key={r._id}
              data-testid={`${PORTAL.reportCard}-${i}`}
              className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:border-blue-200 transition-all">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-blue-900" />
                  </div>
                  <div className="min-w-0">
                    {patientName && (
                      <p className="text-sm font-semibold text-slate-900 truncate">{patientName}</p>
                    )}
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock size={11} /> {fmtDate(r.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <StatusBadge status={r.overall_status} />
                  {r.abnormal_count > 0 && (
                    <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                      {r.abnormal_count} abnormal
                    </span>
                  )}
                </div>
              </div>

              {r.overview && (
                <p className="text-xs text-slate-500 mt-3 leading-relaxed line-clamp-2">{r.overview}</p>
              )}

              <div className="mt-4 pt-3 border-t border-slate-100">
                <Link
                  to={shareUrl}
                  data-testid={`${PORTAL.viewReportButton}-${i}`}
                  className="flex items-center gap-1.5 text-sm font-medium text-blue-900 hover:underline">
                  View full report <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 text-center">
        <button
          data-testid={PORTAL.requestNewLinkButton}
          onClick={onRequestNew}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 mx-auto transition-colors">
          <RefreshCw size={12} /> Request a new access link
        </button>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function PortalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  // View states: "entry" | "sent" | "loading" | "reports" | "error"
  const [view,         setView]    = useState(token ? "loading" : "entry");
  const [sentEmail,    setSentEmail] = useState("");
  const [reports,      setReports]   = useState([]);
  const [portalEmail,  setPortalEmail] = useState("");
  const [errorMsg,     setErrorMsg]   = useState("");

  useEffect(() => {
    if (!token) return;
    setView("loading");
    axios.get(`${API}/portal/reports`, { params: { token } })
      .then(({ data }) => {
        setPortalEmail(data.email);
        setReports(data.reports);
        setView("reports");
      })
      .catch((err) => {
        const detail = err.response?.data?.detail;
        setErrorMsg(typeof detail === "string" ? detail : "Invalid or expired link.");
        setView("error");
      });
  }, [token]);

  const handleSent = (email) => {
    setSentEmail(email);
    setView("sent");
  };

  const handleRequestNew = () => {
    setSearchParams({});
    setView("entry");
    setReports([]);
    setPortalEmail("");
    setErrorMsg("");
  };

  return (
    <div data-testid={PORTAL.page} className="min-h-screen bg-slate-50">
      {/* Minimal header */}
      <div className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-blue-900 flex items-center justify-center">
              <Activity size={14} className="text-white" />
            </div>
            <span className="font-semibold text-slate-900 text-base" style={{ fontFamily: "Outfit, sans-serif" }}>
              LabReport <span className="text-blue-900">AI</span>
            </span>
            <span className="text-slate-300 mx-1 hidden sm:inline">·</span>
            <span className="text-slate-500 text-sm hidden sm:inline">Patient Portal</span>
          </div>
          <Link to="/auth" className="text-xs text-slate-400 hover:text-blue-900 transition-colors">
            Doctor sign in →
          </Link>
        </div>
      </div>

      {/* Content */}
      <main className="flex flex-col items-center justify-center min-h-[calc(100vh-65px)] px-4 py-12">
        {view === "entry" && <RequestAccessScreen onSent={handleSent} />}

        {view === "sent" && <EmailSentScreen email={sentEmail} onBack={() => setView("entry")} />}

        {view === "loading" && (
          <div className="flex flex-col items-center gap-3">
            <div className="w-9 h-9 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-400 text-sm">Loading your reports…</p>
          </div>
        )}

        {view === "error" && (
          <div className="w-full max-w-md text-center">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
              <AlertCircle size={26} className="text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-700 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              Link unavailable
            </h2>
            <p data-testid={PORTAL.errorMessage} className="text-slate-400 text-sm mb-6">{errorMsg}</p>
            <button
              data-testid={PORTAL.requestNewLinkButton}
              onClick={handleRequestNew}
              className="bg-blue-900 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors">
              Request a new link
            </button>
          </div>
        )}

        {view === "reports" && (
          <ReportsScreen
            email={portalEmail}
            reports={reports}
            onRequestNew={handleRequestNew}
          />
        )}
      </main>
    </div>
  );
}
